class CardVariantPicker extends HTMLElement {
  constructor() {
    super();
    this.variants = [];
    this.preorderVariants = [];
    this.currentVariant = null;
    this.currentPreorderVariant = null;
    this.buyType = 'regular';
    this.hasPreorder = false;
  }

  connectedCallback() {
    const jsonEl = this.querySelector('.card-variant-json');
    if (jsonEl) {
      try {
        this.variants = JSON.parse(jsonEl.textContent);
      } catch (e) {
        console.error('[CardVariantPicker] Failed to parse variants', e);
      }
    }

    const preorderJsonEl = this.querySelector('.card-preorder-json');
    if (preorderJsonEl) {
      try {
        this.preorderVariants = JSON.parse(preorderJsonEl.textContent);
        this.hasPreorder = this.preorderVariants.length > 0;
        // Pre-order is the default/first option for pre-order products
        if (this.hasPreorder) {
          this.buyType = 'preorder';
        }
      } catch (e) {
        console.error('[CardVariantPicker] Failed to parse preorder variants', e);
      }
    }

    this.optionInputs = this.querySelectorAll('.card-variant-picker__option input[type="radio"]');
    this.atcButton = this.querySelector('.card-variant-actions__atc');
    this.atcText = this.querySelector('.card-atc-text');
    this.quantityInput = this.querySelector('.quantity__input');
    this.toggleBtns = this.querySelectorAll('.card-variant-actions__toggle-btn');
    this.messageEl = this.querySelector('.card-variant-actions__message');

    // Prevent all clicks inside this element from bubbling to the card overlay link
    this.addEventListener('click', (e) => e.stopPropagation());

    this.optionInputs.forEach(input => {
      input.addEventListener('change', this.onVariantChange.bind(this));
    });

    if (this.atcButton) {
      this.atcButton.addEventListener('click', this.onAddToCart.bind(this));
    }

    this.toggleBtns.forEach(btn => {
      btn.addEventListener('click', this.onToggleBuyType.bind(this));
    });

    this.updateCurrentVariant();
    this.updateAvailability();
    this.updateButton();
    this.updatePrice();
  }

  getSelectedOptions() {
    const options = [];
    const fieldsets = this.querySelectorAll('.card-variant-picker__option');
    fieldsets.forEach(fieldset => {
      const checked = fieldset.querySelector('input:checked');
      if (checked) {
        options.push(checked.value);
      }
    });
    return options;
  }

  onVariantChange() {
    this.updateCurrentVariant();
    this.updateAvailability();
    this.updateButton();
    this.updatePrice();
  }

  updateCurrentVariant() {
    const selectedOptions = this.getSelectedOptions();
    let variantIndex;

    // Single variant products have no radio buttons, so selectedOptions is empty
    if (selectedOptions.length === 0 && this.variants.length > 0) {
      variantIndex = 0;
    } else {
      variantIndex = this.variants.findIndex(variant => {
        return variant.options.every((opt, index) => opt === selectedOptions[index]);
      });
    }

    this.currentVariant = variantIndex >= 0 ? this.variants[variantIndex] : null;

    // Pre-order variants are paired by index with regular variants
    this.currentPreorderVariant = (variantIndex >= 0 && this.preorderVariants.length > variantIndex)
      ? this.preorderVariants[variantIndex]
      : null;

    if (this.currentVariant && this.atcButton) {
      this.atcButton.dataset.variantId = this.currentVariant.id;
    }
  }

  getActiveVariant() {
    // Main product has the pre-order price, metafield product has the regular/full price
    if (this.buyType === 'regular' && this.currentPreorderVariant) {
      return this.currentPreorderVariant;
    }
    return this.currentVariant;
  }

  getActiveVariantId() {
    if (this.buyType === 'regular' && this.currentPreorderVariant) {
      return this.currentPreorderVariant.id;
    }
    return this.currentVariant ? this.currentVariant.id : null;
  }

  updateAvailability() {
    if (!this.currentVariant) return;

    const selectedOptions = this.getSelectedOptions();
    const fieldsets = this.querySelectorAll('.card-variant-picker__option');

    fieldsets.forEach((fieldset, fieldsetIndex) => {
      const inputs = fieldset.querySelectorAll('input[type="radio"]');
      inputs.forEach(input => {
        const testOptions = [...selectedOptions];
        testOptions[fieldsetIndex] = input.value;

        const matchingVariant = this.variants.find(variant => {
          return variant.options.every((opt, i) => opt === testOptions[i]);
        });

        input.disabled = !matchingVariant || !matchingVariant.available;
      });
    });
  }

  updateButton() {
    if (!this.atcButton || !this.atcText) return;

    const activeVariant = this.getActiveVariant();

    if (!activeVariant) {
      this.atcButton.disabled = true;
      this.atcText.textContent = 'Unavailable';
      return;
    }

    if (!activeVariant.available) {
      this.atcButton.disabled = true;
      this.atcText.textContent = this.getSoldOutText();
      return;
    }

    this.atcButton.disabled = false;
    this.atcText.textContent = this.buyType === 'preorder'
      ? this.getPreOrderText()
      : this.getAddToCartText();
  }

  updatePrice() {
    const activeVariant = this.getActiveVariant();
    if (!activeVariant) return;

    const card = this.closest('.card-wrapper');
    if (!card) return;

    const priceEl = card.querySelector('.price__regular .price-item--regular');
    if (priceEl) {
      priceEl.innerHTML = this.formatMoney(activeVariant.price);
    }

    const comparePriceEl = card.querySelector('.price__sale .price-item--sale');
    const compareRegularEl = card.querySelector('.price__sale .price-item--regular');
    const priceWrapper = card.querySelector('.price');

    // For regular mode (full price from metafield product), use main variant's compare_at as "was" price
    const compareAt = this.buyType === 'regular' && this.currentVariant
      ? (this.currentVariant.compare_at_price || this.currentVariant.price)
      : activeVariant.compare_at_price;

    const savingsBadge = card.querySelector('.price__badge-sale');

    if (compareAt && compareAt > activeVariant.price) {
      if (priceWrapper) priceWrapper.classList.add('price--on-sale');
      if (comparePriceEl) comparePriceEl.innerHTML = this.formatMoney(activeVariant.price);
      if (compareRegularEl) compareRegularEl.innerHTML = this.formatMoney(compareAt);

      if (savingsBadge) {
        const savingsAmount = compareAt - activeVariant.price;
        savingsBadge.textContent = '\u2013' + this.formatMoney(savingsAmount);
        savingsBadge.style.display = '';
      }
    } else {
      if (priceWrapper) priceWrapper.classList.remove('price--on-sale');
      if (priceEl) priceEl.innerHTML = this.formatMoney(activeVariant.price);
      if (savingsBadge) savingsBadge.style.display = 'none';
    }
  }

  formatMoney(cents) {
    let formatted;
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      formatted = Shopify.formatMoney(cents);
    } else {
      formatted = (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    }
    return formatted.replace(/[.,]00(?=\s*$)/, '');
  }

  onToggleBuyType(e) {
    const btn = e.currentTarget;
    this.buyType = btn.dataset.buyType;

    this.toggleBtns.forEach(b => b.classList.remove('active'));
    btn.classList.add('active');

    this.updateButton();
    this.updatePrice();
    this.updateMessage();
  }

  updateMessage() {
    if (!this.messageEl) return;
    const preorderMsg = this.messageEl.dataset.preorderMessage || '';
    const buynowMsg = this.messageEl.dataset.buynowMessage || '';
    this.messageEl.textContent = this.buyType === 'preorder' ? preorderMsg : buynowMsg;
  }

  onAddToCart() {
    const activeVariant = this.getActiveVariant();
    if (!activeVariant || !activeVariant.available) return;

    const quantity = this.quantityInput ? parseInt(this.quantityInput.value) || 1 : 1;
    const variantId = activeVariant.id;

    this.setLoading(true);

    // Get section IDs so the mini-cart can re-render with fresh HTML
    const miniCart = document.querySelector('mini-cart');
    const sections = (miniCart && typeof miniCart.getSectionsToRender === 'function')
      ? miniCart.getSectionsToRender().map(s => s.section || s.id)
      : [];

    const formData = {
      items: [{
        id: variantId,
        quantity: quantity
      }]
    };

    if (sections.length > 0) {
      formData.sections = sections;
      formData.sections_url = window.location.pathname;
    }

    fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData)
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Add to cart failed');
        }
        return response.json();
      })
      .then(data => {
        this.setLoading(false);

        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'card-variant-picker',
            productVariantId: variantId,
          });
        }

        const openCart = () => {
          const mc = document.querySelector('mini-cart');
          if (mc) {
            if (typeof mc.open === 'function') {
              mc.open();
            } else {
              const details = mc.querySelector('details');
              if (details) details.setAttribute('open', 'true');
            }
          } else {
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer && typeof cartDrawer.open === 'function') {
              cartDrawer.open();
            }
          }
        };

        if (miniCart && typeof miniCart.renderContents === 'function') {
          try {
            miniCart.renderContents(data);
          } catch (e) { /* silent */ }
        }

        setTimeout(openCart, 300);
      })
      .catch(err => {
        console.error('[CardVariantPicker] Add to cart error:', err);
        this.setLoading(false);
      });
  }

  setLoading(loading) {
    if (!this.atcButton) return;

    if (loading) {
      this.atcButton.setAttribute('aria-disabled', 'true');
      this.atcButton.classList.add('loading');
    } else {
      this.atcButton.removeAttribute('aria-disabled');
      this.atcButton.classList.remove('loading');
    }
  }

  getAddToCartText() {
    return 'Buy Now';
  }

  getPreOrderText() {
    return 'Pre-Order';
  }

  getSoldOutText() {
    return window.__cardVariantI18n?.soldOut || 'Sold out';
  }
}

customElements.define('card-variant-picker', CardVariantPicker);
